import React from 'react'
import { useFormContext } from 'react-hook-form'
import * as R from 'remeda'

import { SegmentRouteArrow } from '~/components/features/SegmentRouteArrow'
import { type CustomerStrategySegmentRoute } from '~/gql/graphql'
import { reject } from '~/utils'

import { type InputValues, type StrategyType } from '../../utils'

import styles from './Arrows.css'
import {
  cellHeight,
  cellWidth,
  computeArrowPaths,
  gap,
  type Position,
} from './utils'

type ArrowParam = {
  position: Position
  segmentRoute: CustomerStrategySegmentRoute
  strategyTypes: StrategyType[]
}

const allArrowParams: ArrowParam[] = [
  {
    position: 'upper',
    segmentRoute: { fromSegmentNumber: 9, toSegmentNumber: 3 },
    strategyTypes: ['new', 'free'],
  },
  {
    position: 'lower',
    segmentRoute: { fromSegmentNumber: 9, toSegmentNumber: 7 },
    strategyTypes: ['new', 'free'],
  },
  {
    position: 'lower',
    segmentRoute: { fromSegmentNumber: 7, toSegmentNumber: 3 },
    strategyTypes: ['new', 'free'],
  },
  {
    position: 'center',
    segmentRoute: { fromSegmentNumber: 5, toSegmentNumber: 3 },
    strategyTypes: ['new', 'free'],
  },
  {
    position: 'lower',
    segmentRoute: { fromSegmentNumber: 3, toSegmentNumber: 1 },
    strategyTypes: ['existing', 'free'],
  },
  {
    position: 'lower',
    segmentRoute: { fromSegmentNumber: 8, toSegmentNumber: 7 },
    strategyTypes: ['new', 'free'],
  },
  {
    position: 'upper',
    segmentRoute: { fromSegmentNumber: 8, toSegmentNumber: 3 },
    strategyTypes: ['new', 'free'],
  },
  {
    position: 'lower',
    segmentRoute: { fromSegmentNumber: 6, toSegmentNumber: 5 },
    strategyTypes: ['new', 'free'],
  },
  {
    position: 'lower',
    segmentRoute: { fromSegmentNumber: 6, toSegmentNumber: 3 },
    strategyTypes: ['new', 'free'],
  },
  {
    position: 'lower',
    segmentRoute: { fromSegmentNumber: 4, toSegmentNumber: 3 },
    strategyTypes: ['existing', 'free'],
  },
  {
    position: 'lower',
    segmentRoute: { fromSegmentNumber: 4, toSegmentNumber: 1 },
    strategyTypes: ['existing', 'free'],
  },
  {
    position: 'lower',
    segmentRoute: { fromSegmentNumber: 2, toSegmentNumber: 1 },
    strategyTypes: ['existing', 'free'],
  },
]

export type ArrowsProps = {
  index: number
}

export const Arrows = ({ index }: ArrowsProps) => {
  const namePrefix = `strategies.${index}` as const
  const { getValues, setValue, watch } = useFormContext<InputValues>()
  const [strategyType, showArrow, segmentRoutes] = watch([
    `${namePrefix}.strategyType`,
    `${namePrefix}.showArrow`,
    `${namePrefix}.segmentRoutes`,
  ])

  const handleClick = React.useCallback(
    (segmentRoute: CustomerStrategySegmentRoute) => {
      const name = `strategies.${index}.segmentRoutes` as const
      const current = getValues(name)
      setValue(
        name,
        current.some(R.isDeepEqual(segmentRoute))
          ? reject(current, R.isDeepEqual(segmentRoute))
          : R.concat(current, [segmentRoute]),
      )
    },
    [getValues, index, setValue],
  )

  const arrowParams = React.useMemo(
    () =>
      allArrowParams.filter(({ strategyTypes }) =>
        strategyTypes.includes(strategyType),
      ),
    [strategyType],
  )

  if (!showArrow) {
    return null
  }

  return (
    <svg
      className={styles.root}
      height={cellHeight * 2 + gap}
      stroke="var(--colorFontLighter)"
      strokeOpacity={0}
      strokeWidth={12}
      viewBox={`0 0 ${(cellWidth + gap) * 5 - gap} ${
        (cellHeight + gap) * 2 - gap
      }`}
      width={cellWidth * 5 + gap * 4}
    >
      {arrowParams.map(({ position, segmentRoute }) => {
        const isActive = segmentRoutes.some(R.isDeepEqual(segmentRoute))
        const paths = computeArrowPaths(position, segmentRoute)

        return (
          <SegmentRouteArrow
            key={`${segmentRoute.fromSegmentNumber}-${segmentRoute.toSegmentNumber}`}
            isActive={isActive}
            isInActive={0 < segmentRoutes.length && !isActive}
            paths={paths}
            onClick={() => handleClick(segmentRoute)}
          />
        )
      })}
    </svg>
  )
}
